import { AppBannerType, useAppBanner } from '@/components/AppBanner';
import anonymous from '@/images/anonymous.svg';
import type { Credit } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { useCreditQuery } from '@/state/credits/credits';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import ExpandableButton from '../Buttons/ExpandableButton';
import { ProfilePopup } from '../ProfilePopup';

const ProfileContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
`;

const ProfileText = styled.div`
    font-size: 1.6rem;
    text-align: start;
`;
const ProfileName = styled.div``;

const PositionedProfilePopup = styled(ProfilePopup)`
    bottom: 40px;
`;

const useCreditsFinishingAppBanner = (enabled: boolean, credits?: Credit) => {
    const showCreditsFinishingAppBannerIsEnabled = useFlag('credits_finishing_banner_is_enabled', false);
    const showCreditsFinishingAppBannerPercentage = useFlag('credits_finishing_banner_percentage', 75);
    const { open } = useAppBanner();
    const shouldShow =
        showCreditsFinishingAppBannerIsEnabled &&
        enabled &&
        credits &&
        credits.total - credits.remaining >= credits.total * (showCreditsFinishingAppBannerPercentage / 100);

    useEffect(() => {
        if (shouldShow) {
            open({ type: AppBannerType.CreditsFinishing });
        }
    }, [shouldShow]);
};

interface Props {
    sidebarWidth: string;
    hideDetails?: boolean;
    toggleOpen: (state: boolean) => void; // todo move control to Profile component after removing old sidebar
    isOpen: boolean; // todo move control to Profile component after removing old sidebar
}

export const Profile = ({ sidebarWidth, toggleOpen, isOpen, hideDetails }: Props) => {
    const { user, isAnonymous } = useAuth();
    const { data: credits, isFetched } = useCreditQuery();
    const showBanner = isFetched && !isAnonymous;
    useCreditsFinishingAppBanner(showBanner, credits);
    const profileName = isAnonymous ? 'Guest' : `${user?.given_name ?? ''} ${user?.family_name ?? ''}`;

    return (
        <>
            <ExpandableButton
                id="profile_data"
                className="chameleon-profile-credits"
                ariaLabel="toggle profile"
                icon={user?.picture ?? anonymous}
                isImage
                isOpen={true}
                iconStyleOverride={theme => ({
                    padding: 0,
                    '&::after': {
                        borderRadius: '50%',
                        border: `1px solid ${theme.colors.secondary}`,
                        backgroundSize: isAnonymous ? '70%' : 'contain',
                    },
                })}
                onClick={() => toggleOpen(!isOpen)}>
                {!hideDetails && (
                    <ProfileContainer>
                        <ProfileText>
                            <ProfileName>{profileName}</ProfileName>
                        </ProfileText>
                    </ProfileContainer>
                )}
            </ExpandableButton>
            <PositionedProfilePopup isOpen={isOpen} left={sidebarWidth} onOutsideClick={() => toggleOpen(false)} />
        </>
    );
};
